import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Container,
  Select,
  FormErrorMessage,
  IconButton,
  HStack,
  Flex,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Checkbox,
  Grid,
  GridItem,
  useMediaQuery,
  Divider,
  Stack,
  Tooltip,
  FormHelperText,
  Textarea,
  TagCloseButton,
  TagLabel,
  Tag,
  Wrap,
  WrapItem,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useDisclosure as useMenuDisclosure
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, ChevronLeftIcon, InfoIcon, ChevronDownIcon } from '@chakra-ui/icons';
import supabase from '../utils/supabaseUtils';

export function EventForm() {
  const { eventId } = useParams(); // Get eventId from URL if in edit mode
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [previewImage, setPreviewImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLargerThan768] = useMediaQuery('(min-width: 768px)');
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'active',
    event_type: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    event_image: '',
    templates: [],
    questions: [],
    tags: []
  });
  const [errors, setErrors] = useState({});

  // Predefined list of sports tags
  const sportsTags = [
    "Baseball", "Basketball", "Football", "Soccer", "Volleyball", 
    "Tennis", "Golf", "Swimming", "Track & Field", "Cross Country",
    "Lacrosse", "Hockey", "Wrestling", "Gymnastics", "Cheerleading",
    "Softball", "Field Hockey", "Rugby", "Water Polo", "Badminton",
    "Cricket", "Sailing", "Skiing", "Snowboarding", "Martial Arts",
    "Boxing", "Cycling", "Running", "Triathlon", "Bowling"
  ];

  // Fetch templates and existing event data if in edit mode
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch templates
        const { data: templateData, error: templateError } = await supabase
          .from('templates')
          .select('id, name, thumbnail_url')
          .order('name');
          
        if (templateError) throw templateError;
        setTemplates(templateData || []);
        
        // If eventId exists, fetch the event data (edit mode)
        if (eventId) {
          setIsEditMode(true);
          const { data: eventData, error: eventError } = await supabase
            .from('events')
            .select('*')
            .eq('id', eventId)
            .single();
          
          if (eventError) throw eventError;
          
          if (eventData) {
            // Parse template IDs and questions
            let templateIds = [];
            if (eventData.templates) {
              templateIds = eventData.templates.split(',').filter(id => id);
            }
            
            // Log the event data being fetched
            console.log('Fetched event data:', eventData);
            console.log('Event status from database:', eventData.status);
            
            let questions = [];
            if (eventData.questions) {
              try {
                questions = typeof eventData.questions === 'string' 
                  ? JSON.parse(eventData.questions) 
                  : eventData.questions;
              } catch (e) {
                console.error('Error parsing questions JSON:', e);
              }
            }
            
            // Parse tags
            let tags = [];
            if (eventData.tags) {
              try {
                tags = typeof eventData.tags === 'string' 
                  ? JSON.parse(eventData.tags) 
                  : (Array.isArray(eventData.tags) ? eventData.tags : []);
              } catch (e) {
                console.error('Error parsing tags JSON:', e);
              }
            }
            
            // Set preview image if available
            if (eventData.event_image) {
              setPreviewImage(eventData.event_image);
            }
            
            // Update form data with event data
            setFormData({
              name: eventData.name || '',
              description: eventData.description || '',
              start_date: eventData.start_date ? eventData.start_date.split('T')[0] : '',
              end_date: eventData.end_date ? eventData.end_date.split('T')[0] : '',
              status: eventData.status || 'active',
              event_type: eventData.event_type || '',
              // Check if it's a standard event type; if not, it's a custom type
              custom_event_type: !['tournament', 'clinic', 'camp', 'league', 'game', 'tryout', 'practice', 'other'].includes(eventData.event_type) ? 
                eventData.event_type : '',
              address1: eventData.address1 || eventData.address || '',
              address2: eventData.address2 || '',
              city: eventData.city || '',
              state: eventData.state || '',
              zip: eventData.zip || '',
              event_image: eventData.event_image || '',
              templates: templateIds,
              questions: questions,
              tags: tags
            });
            
            // Log the form status to ensure it's correctly initialized
            console.log('Form status initialized to:', eventData.status || 'active');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingTemplates(false);
      }
    }
    
    fetchData();
  }, [eventId, toast]);

  const handleTemplateSelection = (templateId) => {
    setFormData(prev => {
      const isSelected = prev.templates.includes(templateId);
      
      if (isSelected) {
        // Remove template if already selected
        return {
          ...prev,
          templates: prev.templates.filter(id => id !== templateId)
        };
      } else {
        // Add template if not already selected
        return {
          ...prev,
          templates: [...prev.templates, templateId]
        };
      }
    });
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          id: crypto.randomUUID(),
          text: '',
          type: 'text',
          required: false,
          options: []
        }
      ]
    }));
  };

  const removeQuestion = (questionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
  };

  const updateQuestion = (questionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, [field]: value } : q
      )
    }));
  };

  const addOption = (questionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { ...q, options: [...(q.options || []), ''] }
          : q
      )
    }));
  };

  const removeOption = (questionId, optionIndex) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { 
              ...q, 
              options: q.options.filter((_, index) => index !== optionIndex)
            }
          : q
      )
    }));
  };

  const updateOption = (questionId, optionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? {
              ...q,
              options: q.options.map((opt, idx) => 
                idx === optionIndex ? value : opt
              )
            }
          : q
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Event name is required';
    if (!formData.start_date) newErrors.start_date = 'Start date is required';
    if (!formData.end_date) newErrors.end_date = 'End date is required';
    
    // Validate event type
    if (!formData.event_type) {
      newErrors.event_type = 'Event type is required';
    } else if (formData.event_type === 'other') {
      // If "other" is selected but no custom type is specified
      if (!formData.custom_event_type || !formData.custom_event_type.trim()) {
        newErrors.custom_event_type = 'Please specify the event type';
      } else {
        // Set the event_type to the custom value before submission
        setFormData(prev => ({
          ...prev,
          event_type: formData.custom_event_type.trim()
        }));
      }
    }
    
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zip) newErrors.zip = 'ZIP code is required';
    if (!formData.address1) newErrors.address1 = 'Address is required';
    
    // Log validation errors
    if (Object.keys(newErrors).length > 0) {
      console.error('Form validation errors:', newErrors);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    // Log when status is being changed
    if (field === 'status') {
      console.log('Status changing from', formData.status, 'to', value);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPG, PNG, GIF, or WebP image.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      setUploading(true);
      
      // Get current user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in to upload images');
      
      // Generate file path
      const fileExt = file.name.split('.').pop();
      const fileName = `event_${Date.now()}.${fileExt}`;
      const filePath = `event_images/${user.id}/${fileName}`;
      
      // Upload file
      const { error: uploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
        
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(filePath);
        
      if (!urlData || !urlData.publicUrl) throw new Error('Failed to get image URL');
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        event_image: urlData.publicUrl
      }));
      
      // Show preview
      setPreviewImage(urlData.publicUrl);
      
      toast({
        title: 'Upload successful',
        description: 'Image uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload image',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If "other" is selected, update the event_type with the custom value
    if (formData.event_type === 'other' && formData.custom_event_type?.trim()) {
      setFormData(prev => ({
        ...prev,
        event_type: formData.custom_event_type.trim()
      }));
      
      // Allow state update to complete before validation
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('You must be logged in to manage events');
      }
      
      // Prepare the event data - Use the custom event type if "other" was selected
      const finalEventType = formData.event_type === 'other' && formData.custom_event_type
        ? formData.custom_event_type.trim()
        : formData.event_type;
      
      const eventData = {
        name: formData.name,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: formData.status,
        event_type: finalEventType,
        address1: formData.address1,
        address2: formData.address2 || '',
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        event_image: formData.event_image,
        templates: formData.templates?.join(',') || '',
        questions: JSON.stringify(formData.questions || []),
        tags: JSON.stringify(formData.tags || []),
        updated_by: user.id
      };
      
      console.log('Form state before submission:', formData);
      console.log('Submitting event with data:', eventData);
      console.log('Event status being submitted:', eventData.status);
      console.log('Event image URL being submitted:', eventData.event_image);
      
      let result;
      if (isEditMode) {
        // Update existing event
        console.log('Updating existing event with ID:', eventId);
        result = await supabase
          .from('events')
          .update(eventData)
          .eq('id', eventId)
          .select()
          .single();
      } else {
        // Create new event
        eventData.created_by = user.id; // Only set created_by for new events
        console.log('Creating new event with data:', eventData);
        result = await supabase
          .from('events')
          .insert(eventData)
          .select()
          .single();
      }
      
      const { data, error } = result;
      if (error) throw error;

      console.log(`Event ${isEditMode ? 'updated' : 'created'} successfully:`, data);
      toast({
        title: 'Success',
        description: `Event ${isEditMode ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Redirect to dashboard after successful submission
      navigate('/dashboard', { state: { activeSection: 'admin', adminTab: 'events' } });
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} event:`, error);
      toast({
        title: 'Error',
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} event`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to add a tag
  const addTag = (tag) => {
    if (tag && !formData.tags?.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
    }
  };

  // Function to remove a tag
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
    }));
  };

  // Custom tag input with dropdown
  const TagInput = () => {
    const [customTag, setCustomTag] = useState('');
    const { isOpen, onOpen, onClose } = useMenuDisclosure();
    
    const handleAddCustomTag = () => {
      if (customTag.trim() !== '') {
        addTag(customTag.trim());
        setCustomTag('');
      }
    };

    return (
      <Box mt={4}>
        <FormLabel>Event Tags</FormLabel>
        <Text fontSize="sm" color="gray.600" mb={2}>
          Add tags related to the sports and activities featured in this event. These tags help with organization and can be used by AI tools for improved content generation.
        </Text>
        
        <HStack mb={3}>
          <Input
            placeholder="Enter a custom tag"
            value={customTag}
            onChange={(e) => setCustomTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddCustomTag();
              }
            }}
          />
          <Button
            onClick={handleAddCustomTag}
            colorScheme="blue"
          >
            Add
          </Button>
          <Menu isOpen={isOpen} onClose={onClose}>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              onClick={onOpen}
              variant="outline"
            >
              Sports
            </MenuButton>
            <Portal>
              <MenuList maxH="300px" overflowY="auto">
                {sportsTags.map(sport => (
                  <MenuItem 
                    key={sport}
                    onClick={() => {
                      addTag(sport);
                      onClose();
                    }}
                  >
                    {sport}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </HStack>
        
        <Wrap spacing={2} mb={2}>
          {(formData.tags || []).map(tag => (
            <WrapItem key={tag}>
              <Tag
                size="md"
                borderRadius="full"
                variant="solid"
                colorScheme="blue"
              >
                <TagLabel>{tag}</TagLabel>
                <TagCloseButton onClick={() => removeTag(tag)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      </Box>
    );
  };

  return (
    <Container maxW="container.lg" py={10}>
      <Link to="/dashboard" state={{ activeSection: 'admin', adminTab: 'events' }}>
        <Button leftIcon={<ChevronLeftIcon />} colorScheme="gray" size="sm" mb={6}>
          Back to Dashboard
        </Button>
      </Link>
      
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>{isEditMode ? 'Edit Event' : 'Create New Event'}</Heading>
          <Text color="gray.600">{isEditMode ? 'Update the details for your event' : 'Fill in the details for your event'}</Text>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid templateColumns={isLargerThan768 ? "repeat(2, 1fr)" : "repeat(1, 1fr)"} gap={6}>
            {/* Left Column */}
            <GridItem colSpan={1}>
              <VStack spacing={6} align="stretch">
                <FormControl isRequired isInvalid={!!errors.name}>
                  <FormLabel>Event Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter event name"
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Event Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter a brief description of your event"
                    rows={3}
                  />
                  <FormHelperText>This will be displayed on the event signup page</FormHelperText>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.event_type}>
                  <FormLabel>Event Type</FormLabel>
                  <Select
                    value={['tournament', 'clinic', 'camp', 'league', 'game', 'tryout', 'practice', 'other'].includes(formData.event_type) 
                      ? formData.event_type 
                      : 'other'}
                    onChange={(e) => {
                      if (e.target.value !== 'other') {
                        // If it's not "other", just set the event_type directly
                        handleInputChange('event_type', e.target.value);
                        // Clear any custom event type
                        handleInputChange('custom_event_type', '');
                      } else {
                        // If "other" is selected, set event_type to "other" temporarily
                        handleInputChange('event_type', 'other');
                        // If there was a previous custom value, restore it
                        if (formData.custom_event_type) {
                          handleInputChange('custom_event_type', formData.custom_event_type);
                        }
                      }
                    }}
                    placeholder="Select event type"
                  >
                    <option value="tournament">Tournament</option>
                    <option value="clinic">Clinic</option>
                    <option value="camp">Camp</option>
                    <option value="league">League</option>
                    <option value="game">Game</option>
                    <option value="tryout">Tryout</option>
                    <option value="practice">Practice</option>
                    <option value="other">Other</option>
                  </Select>
                  <FormErrorMessage>{errors.event_type}</FormErrorMessage>
                </FormControl>

                {formData.event_type === 'other' && (
                  <FormControl isRequired isInvalid={!!errors.custom_event_type}>
                    <FormLabel>Specify Event Type</FormLabel>
                    <Input
                      value={formData.custom_event_type || ''}
                      onChange={(e) => {
                        // Only update the custom_event_type field, not the actual event_type yet
                        handleInputChange('custom_event_type', e.target.value);
                      }}
                      placeholder="Enter custom event type"
                    />
                    <FormHelperText>Please specify the type of event you're creating</FormHelperText>
                  </FormControl>
                )}

                <FormControl isRequired isInvalid={!!errors.start_date}>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                  />
                  <FormErrorMessage>{errors.start_date}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.end_date}>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                  />
                  <FormErrorMessage>{errors.end_date}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    Event Image
                    <Tooltip label="This image will be displayed on the event page and in event listings">
                      <InfoIcon ml={1} color="gray.500" />
                    </Tooltip>
                  </FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    sx={{
                      '::file-selector-button': {
                        height: 10,
                        padding: 0,
                        mr: 4,
                        background: 'none',
                        border: 'none',
                        fontWeight: 'bold',
                      },
                    }}
                  />
                  
                  {uploading && <Text mt={2} fontSize="sm">Uploading...</Text>}
                  
                  {previewImage && (
                    <Box mt={2} position="relative">
                      <Image
                        src={previewImage}
                        alt="Event preview"
                        borderRadius="md"
                        maxH="200px"
                        objectFit="cover"
                      />
                    </Box>
                  )}
                </FormControl>
              </VStack>
            </GridItem>

            {/* Right Column */}
            <GridItem colSpan={1}>
              <VStack spacing={6} align="stretch">
                <FormControl isRequired isInvalid={!!errors.address1}>
                  <FormLabel>Address Line 1</FormLabel>
                  <Input
                    value={formData.address1}
                    onChange={(e) => handleInputChange('address1', e.target.value)}
                    placeholder="Enter address"
                  />
                  <FormErrorMessage>{errors.address1}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Address Line 2</FormLabel>
                  <Input
                    value={formData.address2}
                    onChange={(e) => handleInputChange('address2', e.target.value)}
                    placeholder="Enter address line 2 (optional)"
                  />
                </FormControl>

                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <GridItem colSpan={{ base: 3, md: 1 }}>
                    <FormControl isRequired isInvalid={!!errors.city}>
                      <FormLabel>City</FormLabel>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="Enter city"
                      />
                      <FormErrorMessage>{errors.city}</FormErrorMessage>
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 3, md: 1 }}>
                    <FormControl isRequired isInvalid={!!errors.state}>
                      <FormLabel>State</FormLabel>
                      <Input
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="Enter state"
                      />
                      <FormErrorMessage>{errors.state}</FormErrorMessage>
                    </FormControl>
                  </GridItem>

                  <GridItem colSpan={{ base: 3, md: 1 }}>
                    <FormControl isRequired isInvalid={!!errors.zip}>
                      <FormLabel>ZIP Code</FormLabel>
                      <Input
                        value={formData.zip}
                        onChange={(e) => handleInputChange('zip', e.target.value)}
                        placeholder="Enter ZIP code"
                      />
                      <FormErrorMessage>{errors.zip}</FormErrorMessage>
                    </FormControl>
                  </GridItem>
                </Grid>

                <FormControl>
                  <HStack justify="space-between" mb={2}>
                    <FormLabel mb={0}>
                      Template Selection
                      <Tooltip label="Select templates that will be available for this event">
                        <InfoIcon ml={1} color="gray.500" />
                      </Tooltip>
                    </FormLabel>
                    <Button size="sm" onClick={onOpen} colorScheme="blue" variant="outline">
                      Browse Templates
                    </Button>
                  </HStack>
                  
                  {formData.templates.length > 0 ? (
                    <Flex wrap="wrap" gap={2} mt={2}>
                      {formData.templates.map(id => {
                        const template = templates.find(t => t.id === id);
                        return (
                          <Badge 
                            key={id} 
                            colorScheme="blue" 
                            borderRadius="full" 
                            px={3} 
                            py={1}
                            display="flex"
                            alignItems="center"
                          >
                            {template?.name || id}
                            <IconButton
                              icon={<DeleteIcon />}
                              size="xs"
                              ml={1}
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() => handleTemplateSelection(id)}
                              aria-label="Remove template"
                            />
                          </Badge>
                        );
                      })}
                    </Flex>
                  ) : (
                    <Text fontSize="sm" color="gray.500">No templates selected</Text>
                  )}
                </FormControl>
                
                {/* Tag Input */}
                <TagInput />
              </VStack>
            </GridItem>
          </Grid>

          {/* Template selection modal */}
          <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Select Templates</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                {loadingTemplates ? (
                  <Text>Loading templates...</Text>
                ) : templates.length === 0 ? (
                  <Text>No templates available</Text>
                ) : (
                  <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4} mb={4}>
                    {templates.map((template) => (
                      <GridItem key={template.id}>
                        <Box
                          borderWidth={1}
                          borderRadius="md"
                          overflow="hidden"
                          cursor="pointer"
                          onClick={() => handleTemplateSelection(template.id)}
                          borderColor={formData.templates.includes(template.id) ? "blue.500" : "gray.200"}
                          boxShadow={formData.templates.includes(template.id) ? "0 0 0 2px var(--chakra-colors-blue-500)" : "none"}
                        >
                          <Image
                            src={template.thumbnail_url || "https://via.placeholder.com/200x150"}
                            alt={template.name}
                            height="120px"
                            width="100%"
                            objectFit="cover"
                          />
                          <Box p={2}>
                            <Flex justify="space-between" align="center">
                              <Text fontWeight="semibold" noOfLines={1} fontSize="sm">
                                {template.name}
                              </Text>
                              <Checkbox 
                                isChecked={formData.templates.includes(template.id)}
                                onChange={() => {}}
                                colorScheme="blue"
                              />
                            </Flex>
                          </Box>
                        </Box>
                      </GridItem>
                    ))}
                  </Grid>
                )}
                
                <Button onClick={onClose} colorScheme="blue" width="full">
                  Done
                </Button>
              </ModalBody>
            </ModalContent>
          </Modal>

          {/* Questions Section */}
          <Box mt={8}>
            <Divider mb={6} />
            <Stack direction={{ base: "column", md: "row" }} justify="space-between" align="center" mb={4}>
              <Heading size="md">Registration Questions</Heading>
              <Button
                leftIcon={<AddIcon />}
                onClick={addQuestion}
                colorScheme="blue"
                size="sm"
              >
                Add Question
              </Button>
            </Stack>

            {formData.questions.length === 0 ? (
              <Text color="gray.500" mb={4}>No questions added yet. Add questions to collect information from participants.</Text>
            ) : (
              formData.questions.map((question, index) => (
                <Box key={question.id} p={4} borderWidth={1} borderRadius="md" mb={4}>
                  <HStack justify="space-between" mb={4}>
                    <Heading size="sm">Question {index + 1}</Heading>
                    <IconButton
                      icon={<DeleteIcon />}
                      onClick={() => removeQuestion(question.id)}
                      colorScheme="red"
                      size="sm"
                      variant="ghost"
                      aria-label="Delete question"
                    />
                  </HStack>

                  <VStack spacing={4} align="stretch">
                    <FormControl isInvalid={errors.questions?.[index]?.text}>
                      <FormLabel>Question Text</FormLabel>
                      <Input
                        value={question.text}
                        onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
                        placeholder="Enter question text"
                      />
                      {errors.questions?.[index]?.text && (
                        <FormErrorMessage>{errors.questions[index].text}</FormErrorMessage>
                      )}
                    </FormControl>

                    <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                      <FormControl>
                        <FormLabel>Question Type</FormLabel>
                        <Select
                          value={question.type}
                          onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                        >
                          <option value="text">Text</option>
                          <option value="radio">Single Choice</option>
                          <option value="checkbox">Multiple Choice</option>
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Required</FormLabel>
                        <Select
                          value={question.required ? 'true' : 'false'}
                          onChange={(e) => updateQuestion(question.id, 'required', e.target.value === 'true')}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </Select>
                      </FormControl>
                    </Grid>

                    {(question.type === 'radio' || question.type === 'checkbox') && (
                      <Box>
                        <FormControl isInvalid={errors.questions?.[index]?.options}>
                          <HStack justify="space-between" mb={2}>
                            <FormLabel mb={0}>Options</FormLabel>
                            <Button
                              leftIcon={<AddIcon />}
                              onClick={() => addOption(question.id)}
                              size="xs"
                              colorScheme="blue"
                              variant="outline"
                            >
                              Add Option
                            </Button>
                          </HStack>
                          {errors.questions?.[index]?.options && (
                            <FormErrorMessage>{errors.questions[index].options}</FormErrorMessage>
                          )}
                          
                          {(!question.options || question.options.length === 0) && (
                            <Text fontSize="sm" color="gray.500">No options added yet</Text>
                          )}
                          
                          <VStack spacing={2} align="stretch" mt={2}>
                            {question.options?.map((option, optionIndex) => (
                              <HStack key={optionIndex}>
                                <Input
                                  value={option}
                                  onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                                  placeholder={`Option ${optionIndex + 1}`}
                                  size="sm"
                                />
                                <IconButton
                                  icon={<DeleteIcon />}
                                  onClick={() => removeOption(question.id, optionIndex)}
                                  colorScheme="red"
                                  size="sm"
                                  aria-label="Remove option"
                                />
                              </HStack>
                            ))}
                          </VStack>
                        </FormControl>
                      </Box>
                    )}
                  </VStack>
                </Box>
              ))
            )}
          </Box>

          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            isLoading={loading}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
            w="full"
            mt={8}
          >
            {isEditMode ? 'Update Event' : 'Create Event'}
          </Button>
        </form>
      </VStack>
    </Container>
  );
} 